import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { ContentCopy, Delete, Edit, Refresh } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import BackButton from "../../common/back-button";
import CustomTabs from "../../common/custom-tabs";
import AppTable from "../../common/app-table";
import AppChip from "../../common/app-chip";
import ToggleSwitch from "../../common/toggle-switch";
import {
  INSTAGRAM_ACCOUNTS,
  INSTAGRAM_ACCOUNT_MEDIA,
  INSTAGRAM_ACTIVITY,
  INSTAGRAM_RULES,
  INSTAGRAM_RULE_DETAIL,
  INSTAGRAM_RULE_TEST,
} from "../../config/urls";
import { DmPreview, ReplyEditor } from "./RuleMessage";
import InstagramComments from "./InstagramComments";
import InstagramConnectChat from "./InstagramConnectChat";
import { Section, errorMessage, formatDate, useInstagramApi } from "./shared";

// "Instagram Flow" under the sidebar's CONFIGURATION group: DM / comment / story
// automations and what they did. Accounts are connected in "Channel Onboarding".

const TRIGGERS = [
  { value: "dm_keyword", label: "Keyword in DM" },
  { value: "first_message", label: "First message (welcome)" },
  { value: "outside_hours", label: "Outside business hours" },
  { value: "default_reply", label: "Default reply (nothing else matched)" },
  { value: "story_mention", label: "Story mention" },
  { value: "story_reply", label: "Story reply" },
  { value: "comment_keyword", label: "Comment on a post (comment-to-DM)" },
  { value: "ice_breaker", label: "Ice breaker", hidden: true }, // managed in the account's Ice breakers tab
];
const TRIGGER_LABEL = Object.fromEntries(TRIGGERS.map((t) => [t.value, t.label]));
const KEYWORD_TRIGGERS = ["dm_keyword", "story_reply", "comment_keyword"];
const TEST_EVENTS = [
  { value: "dm", label: "DM" },
  { value: "story_mention", label: "Story mention" },
  { value: "story_reply", label: "Story reply" },
  { value: "comment", label: "Comment" },
];

const EMPTY_RULE = {
  account: null,
  name: "",
  trigger_type: "dm_keyword",
  keywords: [],
  match_type: "contains",
  post_scope: "all",
  media_ids: [],
  conditions: {},
  action_type: "reply",
  reply_text: "",
  buttons: [],
  quick_replies: [],
  public_replies: [],
  hide_comment: false,
  once_per_user: false,
  cooldown_minutes: 0,
  daily_cap: 0,
  priority: 0,
  is_active: true,
};

// any_comment is UI-only (the API treats no keywords as any comment); it stops an empty keyword box
// from silently matching every comment.
const PRICE_LIST_TEMPLATE = {
  ...EMPTY_RULE,
  trigger_type: "comment_keyword",
  once_per_user: true,
  any_comment: false,
  name: "Comment-to-DM: price list",
  keywords: ["price", "prices", "cost"],
  reply_text: "Hi {first_name}! Thanks for commenting 🙌 Here's our price list:",
  buttons: [{ title: "See prices", url: "" }],
  public_replies: ["Sent you a DM 📩", "Check your DMs!", "Just messaged you 🙌"],
};

const norm = (s) => s.trim().toLowerCase();

const postScopeLabel = (r) => {
  if (r.post_scope === "selected") return `${r.media_ids.length} selected`;
  if (r.post_scope === "next") return r.media_ids.length ? "Next post (locked)" : "Next post (waiting)";
  return "All posts";
};

const Row = ({ children }) => (
  <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>{children}</Box>
);

const InstagramFlow = () => {
  const { request, notify, toast } = useInstagramApi();

  const [tab, setTab] = useState("automations");
  const [managing, setManaging] = useState(false); // account Manage screen (Overview, Ice breakers, ...)
  const [busy, setBusy] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [rules, setRules] = useState([]);
  const [media, setMedia] = useState([]);
  const [activity, setActivity] = useState({ messages: [], comments: [] });
  const [editing, setEditing] = useState(null); // rule being edited in the drawer
  const [filters, setFilters] = useState({ account: "", trigger: "" });
  const [test, setTest] = useState({ event: "dm", text: "", is_new: false, outside_hours: false, follows_business: false });
  const [testResult, setTestResult] = useState(null);

  const loadRules = useCallback(async () => {
    try {
      setRules((await request("get", INSTAGRAM_RULES)).rules);
    } catch (error) {
      notify(errorMessage(error, "Could not load automations."), "error");
    }
  }, [request, notify]);

  const loadActivity = useCallback(async () => {
    try {
      setActivity(await request("get", INSTAGRAM_ACTIVITY));
    } catch (error) {
      notify(errorMessage(error, "Could not load activity."), "error");
    }
  }, [request, notify]);

  useEffect(() => {
    loadRules();
    request("get", INSTAGRAM_ACCOUNTS)
      .then((data) => setAccounts(data.accounts.filter((a) => a.status !== "DISCONNECTED")))
      .catch(() => setAccounts([]));
  }, [loadRules, request]);

  useEffect(() => {
    if (tab === "activity") loadActivity();
  }, [tab, loadActivity]);

  const loadMedia = async (accountId) => {
    const id = accountId || accounts[0]?.id;
    if (!id) return setMedia([]);
    try {
      setMedia((await request("get", INSTAGRAM_ACCOUNT_MEDIA(id))).media || []);
    } catch {
      setMedia([]); // post picker just stays empty ("all posts")
    }
  };

  const openRule = (rule, copy = false) => {
    const next = rule ? { ...rule } : { ...EMPTY_RULE };
    if (rule?.trigger_type === "comment_keyword") next.any_comment = !rule.keywords.length;
    if (copy) {
      delete next.id;
      next.name = `${rule.name} (copy)`;
      next.is_active = false;
    }
    setEditing(next);
    setTestResult(null);
    loadMedia(next.account);
  };

  const saveRule = async () => {
    if (editing.trigger_type === "comment_keyword" && !editing.any_comment && !editing.keywords.some((k) => k.trim())) {
      notify("Add a keyword, or tick “Any comment”.", "error");
      return;
    }
    setBusy(true);
    try {
      const saved = editing.id
        ? await request("patch", INSTAGRAM_RULE_DETAIL(editing.id), editing)
        : await request("post", INSTAGRAM_RULES, editing);
      setEditing(null);
      notify(saved.warning || "Automation saved.", saved.warning ? "warning" : "success");
      loadRules();
    } catch (error) {
      notify(errorMessage(error, "Could not save the automation."), "error");
    } finally {
      setBusy(false);
    }
  };

  const toggleRule = async (rule) => {
    try {
      await request("patch", INSTAGRAM_RULE_DETAIL(rule.id), { is_active: !rule.is_active });
      loadRules();
    } catch (error) {
      notify(errorMessage(error, "Could not update the automation."), "error");
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete "${rule.name}"?`)) return;
    try {
      await request("delete", INSTAGRAM_RULE_DETAIL(rule.id));
      loadRules();
    } catch (error) {
      notify(errorMessage(error, "Could not delete the automation."), "error");
    }
  };

  const set = (field) => (event) => setEditing({ ...editing, [field]: event.target.value });
  const setList = (field, separator) => (event) =>
    setEditing({ ...editing, [field]: event.target.value.split(separator) });
  const patch = (fields) => setEditing((rule) => ({ ...rule, ...fields }));
  const setCondition = (key) => (event) => patch({ conditions: { ...editing.conditions, [key]: event.target.value } });
  const isComment = editing?.trigger_type === "comment_keyword";
  const isIceBreaker = editing?.trigger_type === "ice_breaker";
  const accountLabel = (id) => (id ? `@${accounts.find((a) => a.id === id)?.username || id}` : "All accounts");
  const previewAccount = accounts.find((a) => a.id === editing?.account) || accounts[0];

  // Active rules of the same trigger that share a keyword on an overlapping account: priority decides.
  const conflicts =
    editing && KEYWORD_TRIGGERS.includes(editing.trigger_type)
      ? rules.filter(
          (r) =>
            r.id !== editing.id &&
            r.is_active &&
            r.trigger_type === editing.trigger_type &&
            (r.account == null || editing.account == null || r.account === editing.account) &&
            r.keywords.some((k) => editing.keywords.filter((e) => e.trim()).map(norm).includes(norm(k))),
        )
      : [];

  const runTest = async () => {
    try {
      setTestResult(await request("post", INSTAGRAM_RULE_TEST, { ...test, account: editing.account || accounts[0]?.id }));
    } catch (error) {
      notify(errorMessage(error, "Test failed."), "error");
    }
  };

  const sampleInbound = () => {
    const t = editing.trigger_type;
    if (t === "story_mention") return "📷 Mentioned you in their story";
    if (t === "ice_breaker") return editing.keywords[0] || "Your question";
    if (t === "story_reply") return `↩ Replied to your story: ${editing.keywords[0] || "Love this!"}`;
    if (t === "comment_keyword") return null; // the private reply arrives without a DM from the user
    return editing.keywords.find((k) => k.trim()) || "Hi!";
  };

  // ── Automations tab ───────────────────────────────────────────────────────
  const visibleRules = rules.filter(
    (r) =>
      (!filters.account || r.account === filters.account || r.account == null) &&
      (!filters.trigger || r.trigger_type === filters.trigger),
  );

  const ruleColumns = [
    { id: "name", label: "Name" },
    {
      id: "trigger_type",
      label: "Trigger",
      render: (r) => (r.trigger_type === "comment_keyword"
        ? `${TRIGGER_LABEL[r.trigger_type]} · ${postScopeLabel(r)}`
        : TRIGGER_LABEL[r.trigger_type]),
    },
    { id: "account", label: "Account", render: (r) => accountLabel(r.account) },
    { id: "action_type", label: "Action", render: (r) => (r.action_type === "assign" ? "Hand over" : "Reply") },
    {
      id: "keywords",
      label: "Keywords",
      render: (r) => (r.trigger_type === "ice_breaker" ? `“${r.keywords[0]}”` : r.keywords.length ? r.keywords.join(", ") : "Any"),
    },
    { id: "counts", label: "Triggered / Sent / Failed", render: (r) => `${r.triggered_count} / ${r.sent_count} / ${r.failed_count}` },
    { id: "is_active", label: "Active", render: (r) => <ToggleSwitch checked={r.is_active} onChange={() => toggleRule(r)} /> },
    {
      id: "actions",
      label: "",
      align: "right",
      render: (r) => (
        <>
          <IconButton size="small" onClick={() => openRule(r)}><Edit fontSize="small" /></IconButton>
          {r.trigger_type !== "ice_breaker" && (
            <IconButton size="small" title="Duplicate" onClick={() => openRule(r, true)}><ContentCopy fontSize="small" /></IconButton>
          )}
          <IconButton size="small" onClick={() => deleteRule(r)}><Delete fontSize="small" /></IconButton>
        </>
      ),
    },
  ];

  const renderAutomations = () => (
    <>
      {accounts.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No Instagram account is connected. Connect one in Channel Onboarding.
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" mb={2}>
        For each message, keyword rules are tried first, then the welcome, out-of-hours and default replies. Within a
        trigger, the highest priority wins. Bots only reply inside Meta's 24 h window. Comment rules send one private
        DM per comment (within 7 days). Ice breakers are set up in each account's Ice breakers tab.
      </Typography>
      <Box display="flex" alignItems="center" mb={2} gap={1.5} flexWrap="wrap">
        <TextField select size="small" label="Account" value={filters.account} sx={{ minWidth: 180 }}
          onChange={(e) => setFilters({ ...filters, account: e.target.value })}>
          <MenuItem value="">All accounts</MenuItem>
          {accounts.map((a) => <MenuItem key={a.id} value={a.id}>@{a.username}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Trigger" value={filters.trigger} sx={{ minWidth: 220 }}
          onChange={(e) => setFilters({ ...filters, trigger: e.target.value })}>
          <MenuItem value="">All triggers</MenuItem>
          {TRIGGERS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <Box flex={1} />
        <ButtonOne variant="outlined" title="Comment-to-DM template" onClick={() => openRule(PRICE_LIST_TEMPLATE)} />
        <ButtonOne variant="outlined" title="Manage" onClick={() => setManaging(true)} />
        <ButtonOne title="New automation" onClick={() => openRule(null)} />
      </Box>
      <AppTable columns={ruleColumns} rows={visibleRules} emptyMessage="No automations yet" />
    </>
  );

  // Selects show their value (e.g. "All accounts") instead of an empty box with the label inside.
  const selectProps = { select: true, size: "small", fullWidth: true, SelectProps: { displayEmpty: true }, InputLabelProps: { shrink: true } };

  const renderRuleForm = () => (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <BackButton onClick={() => setEditing(null)} />
        <Box flex={1} minWidth={200}>
          <Typography variant="h5" fontWeight={700}>{editing.id ? "Edit automation" : "New automation"}</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose what starts it, who it applies to, and what the bot sends.
          </Typography>
        </Box>
        <ButtonOne variant="outlined" title="Cancel" onClick={() => setEditing(null)} />
        <ButtonOne title={busy ? "Saving…" : "Save automation"} onClick={saveRule} disabled={busy} />
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "minmax(0, 1fr) 380px" }} gap={4} alignItems="start">
        <Box display="flex" flexDirection="column" gap={3} minWidth={0}>
          <Section step={1} title="Trigger" hint="When should this automation run?">
            <TextField label="Automation name" size="small" fullWidth value={editing.name} onChange={set("name")}
              placeholder="e.g. Price enquiry" InputLabelProps={{ shrink: true }} />
            <Row>
              <TextField
                {...selectProps}
                label="Instagram account"
                value={editing.account ?? ""}
                disabled={isIceBreaker}
                onChange={(e) => {
                  const account = e.target.value || null;
                  setEditing({ ...editing, account, media_ids: [] });
                  loadMedia(account);
                }}
              >
                <MenuItem value="">All accounts</MenuItem>
                {accounts.map((a) => <MenuItem key={a.id} value={a.id}>@{a.username}</MenuItem>)}
              </TextField>
              <TextField {...selectProps} label="Trigger" value={editing.trigger_type}
                onChange={(e) => patch({ // a new comment rule starts as "once per person" (spec default)
                  trigger_type: e.target.value,
                  ...(!editing.id && e.target.value === "comment_keyword" ? { once_per_user: true, any_comment: false } : {}),
                })}
                disabled={isIceBreaker}>
                {TRIGGERS.filter((t) => !t.hidden || t.value === editing.trigger_type).map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Row>
            {editing.trigger_type === "outside_hours" && (
              <Alert severity="info">
                Uses the account's Business hours. With routing agents set, the chat is assigned when you open.
              </Alert>
            )}

            {isIceBreaker && (
              <TextField label="Question" size="small" fullWidth value={editing.keywords[0] || ""}
                inputProps={{ maxLength: 80 }} InputLabelProps={{ shrink: true }}
                onChange={(e) => patch({ keywords: [e.target.value] })}
                helperText="Order and publishing are managed in the account's Ice breakers tab." />
            )}

            {isComment && (
              <FormControlLabel
                control={<Checkbox checked={!!editing.any_comment}
                  onChange={(e) => patch({ any_comment: e.target.checked, keywords: [] })} />}
                label={<Typography variant="body2">Any comment (no keyword needed)</Typography>}
              />
            )}

            {KEYWORD_TRIGGERS.includes(editing.trigger_type) && !(isComment && editing.any_comment) && (
              <>
                <Row>
                  <TextField
                    label="Keywords"
                    size="small"
                    fullWidth
                    placeholder="price, cost, rate"
                    InputLabelProps={{ shrink: true }}
                    value={editing.keywords.join(",")}
                    onChange={setList("keywords", ",")}
                    helperText={
                      ["dm_keyword", "comment_keyword"].includes(editing.trigger_type)
                        ? "Comma separated · required"
                        : "Comma separated · empty = any text"
                    }
                  />
                  <TextField {...selectProps} label="Match" value={editing.match_type} onChange={set("match_type")}>
                    <MenuItem value="contains">Contains the word</MenuItem>
                    <MenuItem value="exact">Exact match</MenuItem>
                    <MenuItem value="starts_with">Starts with</MenuItem>
                  </TextField>
                </Row>
                {conflicts.map((r) => (
                  <Alert key={r.id} severity="warning">
                    Shares a keyword with “{r.name}” (priority {r.priority}). The higher priority wins; on a tie, the
                    older automation wins.
                  </Alert>
                ))}
              </>
            )}

            {isComment && (
              <Row>
                <TextField {...selectProps} label="Which posts" value={editing.post_scope || "all"}
                  onChange={(e) => patch({ post_scope: e.target.value, media_ids: [] })}>
                  <MenuItem value="all">All posts</MenuItem>
                  <MenuItem value="selected">Selected posts</MenuItem>
                  <MenuItem value="next">The next post I publish</MenuItem>
                </TextField>
                {editing.post_scope === "selected" && (
                  <TextField
                    {...selectProps}
                    label="Posts"
                    SelectProps={{ multiple: true, displayEmpty: true, renderValue: (ids) => (ids.length ? `${ids.length} selected` : "Pick posts") }}
                    value={editing.media_ids}
                    onChange={set("media_ids")}
                    helperText={media.length ? undefined : "No posts loaded. Refresh them in the account's Manage drawer."}
                  >
                    {media.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        <Checkbox size="small" checked={editing.media_ids.includes(m.id)} />
                        <Avatar variant="rounded" src={m.thumbnail_url || m.media_url} sx={{ width: 32, height: 32, mr: 1 }} />
                        <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>{m.caption || m.permalink}</Typography>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Row>
            )}
            {isComment && editing.post_scope === "next" && (
              <Alert severity="info">
                {editing.media_ids.length ? (
                  <>
                    Locked to the first post published after this rule was saved.{" "}
                    <Link component="button" variant="body2" onClick={() => patch({ media_ids: [] })}>
                      Wait for a newer post instead
                    </Link>
                  </>
                ) : (
                  "Waiting for your next post. It's picked when the first comment on a post published after you save arrives."
                )}
              </Alert>
            )}
          </Section>

          <Section step={2} title="Conditions" hint="Optional. Leave as is to apply to everyone.">
            <Row>
              <TextField {...selectProps} label="Follows your account"
                value={editing.conditions?.follows_business ?? ""} onChange={setCondition("follows_business")}>
                <MenuItem value="">Anyone</MenuItem>
                <MenuItem value={true}>Only followers</MenuItem>
                <MenuItem value={false}>Only non-followers</MenuItem>
              </TextField>
              {!isComment && (
                <TextField {...selectProps} label="Conversation"
                  value={editing.conditions?.conversation ?? ""} onChange={setCondition("conversation")}>
                  <MenuItem value="">New or returning</MenuItem>
                  <MenuItem value="new">New (first message)</MenuItem>
                  <MenuItem value="returning">Returning</MenuItem>
                </TextField>
              )}
            </Row>
            {isComment && (
              <FormControlLabel
                control={<Checkbox checked={!!editing.conditions?.top_level_only}
                  onChange={(e) => patch({ conditions: { ...editing.conditions, top_level_only: e.target.checked } })} />}
                label={<Typography variant="body2">Only top-level comments (ignore replies to other comments)</Typography>}
              />
            )}
            {isComment && [true, false].includes(editing.conditions?.follows_business) && (
              <Typography variant="caption" color="text.secondary">
                Whether someone follows you is only known once they have chatted with you, so first-time commenters
                won't match a follower condition.
              </Typography>
            )}
          </Section>

          <Section step={3} title="Action" hint="What the bot does when this automation runs.">
            <TextField {...selectProps} label="Do this" value={editing.action_type} onChange={set("action_type")}
              helperText={editing.action_type === "assign"
                ? "Pauses the bot for this chat and assigns it round-robin to the account's routing agents."
                : undefined}>
              <MenuItem value="reply">Send a reply</MenuItem>
              <MenuItem value="assign">Hand over to an agent (optional message first)</MenuItem>
            </TextField>
            <ReplyEditor
              value={editing}
              onChange={patch}
              label={isComment ? "Private DM reply" : editing.action_type === "assign" ? "Message before handover (optional)" : "Reply message"}
            />
            {isComment && (
              <TextField
                label="Public comment replies"
                size="small"
                fullWidth
                multiline
                minRows={2}
                InputLabelProps={{ shrink: true }}
                helperText="Optional · one per line, up to 5 · one is picked at random so replies don't look spammy"
                value={editing.public_replies.join("\n")}
                onChange={setList("public_replies", "\n")}
              />
            )}
            {isComment && (
              <FormControlLabel
                control={<Checkbox checked={!!editing.hide_comment} onChange={(e) => patch({ hide_comment: e.target.checked })} />}
                label={<Typography variant="body2">Hide the comment after replying</Typography>}
              />
            )}
          </Section>

          <Section step={4} title="Settings" hint="Limits so people aren't messaged too often.">
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "repeat(3, 1fr)" }} gap={2}>
              <TextField label="Priority" type="number" size="small" value={editing.priority} onChange={set("priority")}
                helperText="Higher runs first" InputLabelProps={{ shrink: true }} />
              <TextField label="Cooldown (minutes)" type="number" size="small" value={editing.cooldown_minutes}
                onChange={set("cooldown_minutes")} helperText="Per user · 0 = off" inputProps={{ min: 0 }}
                InputLabelProps={{ shrink: true }} />
              <TextField label="Daily cap" type="number" size="small" value={editing.daily_cap}
                onChange={set("daily_cap")} helperText="Sends per day · 0 = no cap" inputProps={{ min: 0 }}
                InputLabelProps={{ shrink: true }} />
            </Box>
            <FormControlLabel
              control={<Checkbox checked={editing.once_per_user} onChange={(e) => patch({ once_per_user: e.target.checked })} />}
              label={<Typography variant="body2">Send only once to each person{isComment ? " (recommended)" : ""}</Typography>}
            />
          </Section>
        </Box>

        <Box display="flex" flexDirection="column" gap={3} sx={{ position: { lg: "sticky" }, top: 16 }}>
          <Section title="Preview" hint="How the reply looks on the user's phone.">
            <DmPreview
              account={previewAccount}
              inbound={sampleInbound()}
              rule={editing}
              width={260}
              height={420}
              note={editing.action_type === "assign" ? "Bot pauses · chat goes to an agent" : isComment ? "Private reply to their comment" : null}
            />
          </Section>
          {isComment && (
            <Section title="Under your post" hint="What everyone sees in the comments.">
              <Box sx={{ fontSize: "0.85rem", display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2" sx={{ opacity: editing.hide_comment ? 0.5 : 1 }}>
                  <b>alex</b> {editing.any_comment ? "Love this!" : editing.keywords.find((k) => k.trim()) || "PRICE"}
                  {editing.hide_comment && <Typography component="span" variant="caption" color="text.secondary"> · hidden</Typography>}
                </Typography>
                {editing.public_replies.some((p) => p.trim()) ? (
                  <Typography variant="body2" sx={{ pl: 3 }}>
                    <b>{previewAccount ? previewAccount.username : "you"}</b> {editing.public_replies.find((p) => p.trim())}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>No public reply</Typography>
                )}
              </Box>
            </Section>
          )}
          <Section title="Test rule" hint={`Checks your saved automations${editing.id ? "" : " (save this one first to include it)"}.`}>
            <TextField {...selectProps} label="Event" value={test.event}
              onChange={(e) => setTest({ ...test, event: e.target.value })}>
              {TEST_EVENTS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField size="small" fullWidth label="Message" placeholder="e.g. what's the price?" value={test.text}
              InputLabelProps={{ shrink: true }} onChange={(e) => setTest({ ...test, text: e.target.value })} />
            <Box display="flex" flexDirection="column">
              {[
                ["is_new", "First message from this user"],
                ["outside_hours", "Outside business hours"],
                ["follows_business", "User follows you"],
              ].map(([key, label]) => (
                <FormControlLabel key={key} label={<Typography variant="body2">{label}</Typography>}
                  control={<Checkbox size="small" checked={test[key]} onChange={(e) => setTest({ ...test, [key]: e.target.checked })} />} />
              ))}
            </Box>
            <ButtonOne variant="outlined" title="Run test" onClick={runTest} disabled={!accounts.length} />
            {testResult && (
              <Alert severity={testResult.rule || testResult.handover ? "success" : "info"}>
                {testResult.rule && <b>{testResult.rule.name} · {TRIGGER_LABEL[testResult.rule.trigger_type]}</b>}
                {testResult.message && (
                  <Typography variant="body2" mt={0.5} sx={{ whiteSpace: "pre-wrap" }}>
                    Sends: {testResult.message.text || testResult.message.attachment?.payload?.text}
                  </Typography>
                )}
                {testResult.public_reply && (
                  <Typography variant="body2">Public reply: {testResult.public_reply}</Typography>
                )}
                {testResult.hide_comment && <Typography variant="body2">Then hides the comment.</Typography>}
                {testResult.handover && testResult.rule && (
                  <Typography variant="body2">Then hands the chat to an agent.</Typography>
                )}
                <Typography variant="caption" display="block" mt={0.5}>{testResult.explanation}</Typography>
              </Alert>
            )}
          </Section>
        </Box>
      </Box>
    </Box>
  );

  // ── Activity tab ──────────────────────────────────────────────────────────
  const renderActivity = () => (
    <>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <ButtonOne variant="outlined" startIcon={<Refresh />} title="Refresh" onClick={loadActivity} />
      </Box>
      <Typography fontWeight={700} mb={1}>Comments</Typography>
      <AppTable
        maxHeight={400}
        rows={activity.comments}
        emptyMessage="No comments yet"
        columns={[
          { id: "from_username", label: "From", render: (c) => `@${c.from_username}` },
          { id: "text", label: "Comment" },
          { id: "rule", label: "Rule", render: (c) => c.rule || "—" },
          { id: "status", label: "Status", render: (c) => <AppChip label={c.status} title={c.error} /> },
          { id: "created_at", label: "When", render: (c) => formatDate(c.created_at) },
        ]}
      />
      <Typography fontWeight={700} mt={3} mb={1}>Messages</Typography>
      <AppTable
        maxHeight={400}
        rows={activity.messages}
        emptyMessage="No messages yet"
        columns={[
          { id: "direction", label: "", render: (m) => (m.direction === "in" ? "⬇ In" : "⬆ Out") },
          { id: "igsid", label: "User" },
          { id: "msg_type", label: "Type" },
          { id: "text", label: "Text" },
          { id: "status", label: "Status", render: (m) => <AppChip label={m.sent_by === "instagram_app" ? "from app" : m.status} title={m.error} /> },
          { id: "rule", label: "Rule", render: (m) => m.rule || "—" },
          { id: "created_at", label: "When", render: (m) => formatDate(m.created_at) },
        ]}
      />
    </>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: editing ? "#fafafa" : undefined, minHeight: "100%" }}>
      {editing ? (
        renderRuleForm()
      ) : managing ? (
        <>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <BackButton onClick={() => setManaging(false)} />
            <Box>
              <Typography variant="h5" fontWeight={700}>Manage account</Typography>
              <Typography variant="body2" color="text.secondary">
                Ice breakers, business hours and routing for each Instagram account.
              </Typography>
            </Box>
          </Box>
          <InstagramConnectChat manage />
        </>
      ) : (
        <>
          <Typography variant="h5" fontWeight={700} mb={0.5}>Instagram Flow</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Automate Instagram DMs, comment replies and story replies.
          </Typography>

          <CustomTabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            tabs={[
              { value: "automations", label: "Automations" },
              { value: "comments", label: "Comments" },
              { value: "activity", label: "Activity" },
            ]}
            sx={{ mb: 3, maxWidth: 600 }}
          />

          {tab === "automations" && renderAutomations()}
          {tab === "comments" && <InstagramComments />}
          {tab === "activity" && renderActivity()}
        </>
      )}
      {toast}
    </Box>
  );
};

export default InstagramFlow;
